/**
 * Line-graph geometry: straight segments between observed points, broken where a value
 * is missing. A gap is drawn as a gap — joining across it would show a value nobody
 * measured — and the line is never smoothed, since a curve between points invents
 * values between them too.
 */

export type Point = readonly [number, number];

/** An SVG path through the points, starting a new segment after each missing one. */
export function linePath(points: readonly (Point | null)[]): string {
  const parts: string[] = [];
  let drawing = false;
  for (const point of points) {
    if (point === null) {
      drawing = false;
      continue;
    }
    const [x, y] = point;
    parts.push(`${drawing ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)}`);
    drawing = true;
  }
  return parts.join(" ");
}

/**
 * Indices of points with no neighbour on either side. A segment needs two points, so
 * these would be invisible without a marker; the line graph always marks them.
 */
export function isolatedPoints(points: readonly (Point | null)[]): number[] {
  const isolated: number[] = [];
  for (const [index, point] of points.entries()) {
    if (
      point !== null &&
      (points[index - 1] ?? null) === null &&
      (points[index + 1] ?? null) === null
    ) {
      isolated.push(index);
    }
  }
  return isolated;
}

/**
 * Whether the x labels are all numbers (so the x axis can be drawn to scale), and the
 * numbers if so. Two distinct values are needed for a scale to mean anything.
 */
export function numericLabels(
  labels: readonly string[],
  parse: (label: string) => number | null
): number[] | null {
  const values: number[] = [];
  for (const label of labels) {
    const value = parse(label);
    if (value === null) {
      return null;
    }
    values.push(value);
  }
  return new Set(values).size >= 2 ? values : null;
}

/** True when every value is a whole number from 1000 to 9999 — almost always a year. */
export function looksLikeYears(values: readonly number[]): boolean {
  return (
    values.length > 0 &&
    values.every((v) => Number.isInteger(v) && v >= 1000 && v <= 9999)
  );
}
